import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../context/DialogContext';
import { useTenantSettingsStore } from '../context/TenantSettingsStore';
import { useAuthStore } from '../auth/store';
import type { MaintenanceEventApi } from '../api/models/maintenanceEventApi';

// Time before maintenance starts to show warning
const MAINTENANCE_WARNING_MINUTES = 2;

/**
 * MaintenanceGuard component that:
 * 1. Monitors upcoming maintenance events
 * 2. Shows a warning dialog 2 minutes before maintenance
 * 3. Forces logout when maintenance starts
 */
export const MaintenanceGuard: React.FC = () => {
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  const { t } = useTranslation();
  const dialog = useDialog();
  const settings = useTenantSettingsStore((state) => state.settings);
  const warningShownRef = useRef<string | null>(null);

  // Calculate next maintenance event
  const nextEvent = (() => {
    if (!settings?.maintenanceState?.maintenanceEvents?.length) return null;

    const nowUtc = new Date().toISOString();
    const nextEvents = settings.maintenanceState.maintenanceEvents
      .filter((event) => event.startTime > nowUtc)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    return nextEvents[0] || null;
  })();

  // Show warning dialog
  const showWarningDialog = React.useCallback((event: MaintenanceEventApi) => {
    const start = new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const end = new Date(event.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    dialog.showDialog({
      title: t('maintenance.imminent.title'),
      message: t('maintenance.imminent.message', { start, end }),
      buttons: [{ text: t('common.buttons.ok'), variant: 'primary' }],
    });
  }, [dialog, t]);

  // Setup timers for next maintenance event
  useEffect(() => {
    if (!nextEvent || !authenticated) {
      return () => {};
    }

    const timeUntilStart = new Date(nextEvent.startTime).getTime() - Date.now();
    const warningTime = timeUntilStart - (MAINTENANCE_WARNING_MINUTES * 60 * 1000);
    let warningTimer: NodeJS.Timeout | null = null;
    let maintenanceTimer: NodeJS.Timeout | null = null;

    // Only show warning if we haven't shown it for this specific event
    const shouldShowWarning = warningShownRef.current !== nextEvent.startTime;

    // Show warning dialog if we're within the warning window
    if (shouldShowWarning) {
      if (warningTime > 0) {
        warningTimer = setTimeout(() => {
          showWarningDialog(nextEvent);
          warningShownRef.current = nextEvent.startTime;
        }, warningTime);
      } else if (warningTime <= 0 && timeUntilStart > 0) {
        // If we're already in the warning window but before maintenance starts
        showWarningDialog(nextEvent);
        warningShownRef.current = nextEvent.startTime;
      }
    }

    // Set timer to force logout when maintenance starts
    if (timeUntilStart > 0) {
      maintenanceTimer = setTimeout(() => {
        logout();
      }, timeUntilStart);
    }

    return () => {
      if (warningTimer) clearTimeout(warningTimer);
      if (maintenanceTimer) clearTimeout(maintenanceTimer);
    };
  }, [nextEvent, authenticated, logout, showWarningDialog]);

  return null;
};

export default MaintenanceGuard;
